import React from "react";
import { motion } from "framer-motion";
import { FaUserCircle } from "react-icons/fa";
import { ChatBubbleBottomCenterIcon } from "@heroicons/react/24/solid";
import { AU, CA, CN, IN, NG, US } from "country-flag-icons/react/3x2";

const TestimonialGrid = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 auto-rows-auto md:auto-rows-[250px] gap-4 max-w-7xl mx-auto relative z-10 w-full">
      {/* Row 1 */}
      {/* Arrow absolute to the grid */}
      <div className="absolute -top-24 -left-8 hidden lg:block text-gray-500 font-serif italic text-xl -rotate-6 z-20 pointer-events-none">
        <span className="leading-tight block ml-4">
          see the
          <br />
          insights!
        </span>
        <svg
          width="80"
          height="60"
          viewBox="0 0 100 100"
          className="stroke-gray-500 fill-none mt-2 ml-4"
          strokeWidth="1.5"
        >
          <path d="M10,0 Q10,70 90,90" strokeLinecap="round" />
          <path
            d="M75,82 L90,90 L85,100"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <motion.div
        whileHover={{ y: -4 }}
        className="bg-blue-00/30 rounded-2xl p-6 flex flex-col justify-between col-span-1 shadow-sm transition-all duration-300"
      >
        <div>
          <h3 className="text-4xl lg:text-5xl font-light text-gray-900 mb-2">
            98%
          </h3>
          <p className="text-gray-600 font-medium">visa approval rate</p>
        </div>
        <div className="font-light uppercase text-xl tracking-tight text-gray-900">
          seamlessvisa.
        </div>
      </motion.div>

      <motion.div
        whileHover={{ y: -4 }}
        className="bg-blue-00 rounded-2xl p-6 flex flex-col justify-between col-span-1 shadow-sm transition-all duration-300"
      >
        <div>
          <h3 className="text-4xl lg:text-5xl font-light text-white mb-2">
            10k+
          </h3>
          <p className="text-white/70 font-medium">apps processed</p>
        </div>
        <div className="font-light uppercase text-lg text-white tracking-tight">
          Worldwide
        </div>
      </motion.div>

      {/* Amara Quote (Span 2) */}
      <motion.div
        whileHover={{ y: -4 }}
        className="bg-white rounded-2xl p-6 md:p-8 flex flex-col justify-between md:col-span-2 border border-gray-100 shadow-sm transition-all duration-300"
      >
        <p className="text-gray-900 text-base md:text-lg leading-relaxed">
          "SeamlessVisa helped me to fulfil my goal of studying in Canada into
          reality. The admissions guidance was invaluable, and the visa process
          that seemed overwhelming became manageable with their step-by-step
          service."
        </p>
        <div className="flex items-end justify-between mt-6">
          <div className="flex items-center gap-3">
            <div>
              <p className="font-bold text-gray-900 text-sm">Amara O.</p>
              <p className="text-gray-500 text-xs">
                MBA Student, University of Toronto
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {" "}
            <NG name="Nigeria" className="w-6 h-6" /> →{" "}
            <CA name="Canada" className="w-6 h-6" />{" "}
          </div>
        </div>
      </motion.div>

      {/* Row 2 */}
      {/* Alina Quote (Span 2) */}
      <motion.div
        whileHover={{ y: -4 }}
        className="bg-white rounded-2xl p-6 md:p-8 flex flex-col justify-between md:col-span-2 border border-gray-100 shadow-sm transition-all duration-300"
      >
        <p className="text-gray-900 text-base md:text-lg font leading-relaxed">
          "The AI interview simulator is next-level stuff. I practiced dozens of
          scenarios and felt very prepared for my J-1 visa interview. Approved
          on the first try!"
        </p>
        <div className="flex items-end justify-between mt-6">
          <div className="flex items-center gap-3">
            <div>
              <p className="font-bold text-gray-900 text-sm">Alina K.</p>
              <p className="text-gray-500 text-xs">
                Research Scholar, New Haven
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {" "}
            <IN name="India" className="w-6 h-6" /> →{" "}
            <US name="United States" className="w-6 h-6" />{" "}
          </div>
        </div>
      </motion.div>

      {/* Green Stat */}
      <motion.div
        whileHover={{ y: -4 }}
        className="bg-purple-a8 rounded-2xl p-6 flex flex-col justify-between shadow-sm transition-all duration-300"
      >
        <div>
          <h3 className="text-4xl lg:text-5xl font-light text-white mb-2">
            10x
          </h3>
          <p className="text-white/70 font-medium">faster interview prep</p>
        </div>
        <div className="font-light uppercase text-lg text-white tracking-tight flex items-center gap-2">
          <ChatBubbleBottomCenterIcon className="w-4 h-4" />
          FiBi AI
        </div>
      </motion.div>

      {/* Yellow Stat */}
      <motion.div
        whileHover={{ y: -4 }}
        className="bg-lavender-ea rounded-2xl p-6 flex flex-col justify-between shadow-sm transition-all duration-300"
      >
        <div>
          <h3 className="text-4xl lg:text-5xl font-light text-gray-900 mb-2">
            500+
          </h3>
          <p className="text-gray-600 font-medium">partner universities</p>
        </div>
        <div className="font-light uppercase text-lg text-gray-900 tracking-tight">
          global reach
        </div>
      </motion.div>

      {/* Row 3 */}
      {/* Pink Stat */}
      <motion.div
        whileHover={{ y: -4 }}
        className="bg-[#fce4ec] rounded-2xl p-6 flex flex-col justify-between shadow-sm transition-all duration-300"
      >
        <div>
          <h3 className="text-4xl lg:text-5xl font-light text-gray-900 mb-2">
            24/7
          </h3>
          <p className="text-gray-600 font-medium">expert support</p>
        </div>
        <div className="font-light uppercase text-lg text-gray-900 tracking-tight flex items-center gap-2">
          <FaUserCircle className="text-gray-900 text-xl" />
          Guides
        </div>
      </motion.div>

      {/* Lavender Stat */}
      <motion.div
        whileHover={{ y: -4 }}
        className="bg-[#e8eaf6] rounded-2xl p-6 flex flex-col justify-between shadow-sm transition-all duration-300"
      >
        <div>
          <h3 className="text-4xl lg:text-5xl font-light text-gray-900 mb-2">
            100%
          </h3>
          <p className="text-gray-600 font-medium">satisfaction</p>
        </div>
        <div className="font-light uppercase text-lg text-gray-900 tracking-tight">
          SEAMLESS
        </div>
      </motion.div>

      {/* Andrea Quote (Span 2) */}
      <motion.div
        whileHover={{ y: -4 }}
        className="bg-white rounded-2xl p-6 md:p-8 flex flex-col justify-between md:col-span-2 border border-gray-100 shadow-sm transition-all duration-300"
      >
        <p className="text-gray-900 text-base md:text-lg leading-relaxed">
          "Applying to universities in three different countries felt daunting
          until SeamlessVisa worked with me to develop a cohesive strategy. Now
          I'm pursuing my PhD at my dream school in Australia."
        </p>
        <div className="flex items-end justify-between mt-6">
          <div className="flex items-center gap-3">
            <div>
              <p className="font-bold text-gray-900 text-sm">Andrea M.</p>
              <p className="text-gray-500 text-xs">
                PhD Candidate, University of Melbourne
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {" "}
            <CN name="China" className="w-6 h-6" /> →{" "}
            <AU name="Australia" className="w-6 h-6" />{" "}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Testimonials = () => {
  return (
    <section className="bg-white-f9 py-24 sm:py-32 overflow-hidden flex flex-col items-center">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 mb-16 relative">
        <h2 className="text-center font-lato font-light tracking-tight text-gray-900 text-4xl sm:text-5xl max-w-xl mx-auto leading-tight">
          Real{" "}
          <span className="text-blue-00 italic mx-1">results 🏆</span>{" "}
          from
          <br />
          real customers
        </h2>
      </div>

      <div className="px-4 w-full">
        <TestimonialGrid />
      </div>
    </section>
  );
};

export default Testimonials;
